import React, { useRef } from 'react';

interface LabelItem {
    name: string;
    code?: string;
    quantity: number;
    specialInstructions?: string;
}

interface LabelData {
    orderNumber: string;
    customerName: string;
    customerPhone?: string;
    createdAt: string;
    dueDate?: string;
    items: LabelItem[];
    totalAmount: number;
    paidAmount: number;
    paymentStatus: string;
    status: string;
    specialInstructions?: string;
    siteName?: string;
    sitePhone?: string;
    siteAddress?: string;
    itemCount: number;
}

interface LabelPrintProps {
    data: LabelData;
    labelType: 'garment' | 'receipt';
    onClose: () => void;
}

const LabelPrint: React.FC<LabelPrintProps> = ({ data, labelType, onClose }) => {
    const printRef = useRef<HTMLDivElement>(null);

    const handlePrint = () => {
        window.print();
    };

    const formatDate = (d?: string) =>
        d ? new Date(d).toLocaleDateString('en-GB', { day: '2-digit', month: '2-digit', year: '2-digit' }) : '—';

    const formatTime = (d?: string) =>
        d ? new Date(d).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' }) : '';

    return (
        <>
            {/* Print-specific styles */}
            <style>{`
                @media print {
                    body * { visibility: hidden !important; }
                    .label-print-area, .label-print-area * { visibility: visible !important; }
                    .label-print-area {
                        position: absolute !important;
                        left: 0 !important;
                        top: 0 !important;
                        width: 80mm !important;
                        margin: 0 !important;
                        padding: 0 !important;
                    }
                    .no-print { display: none !important; }
                    @page {
                        size: 80mm auto;
                        margin: 2mm;
                    }
                }
            `}</style>

            {/* Modal overlay — hidden in print */}
            <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4 no-print">
                <div className="bg-white rounded-2xl shadow-2xl max-w-lg w-full max-h-[90vh] overflow-auto">
                    {/* Modal header */}
                    <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
                        <h2 className="text-lg font-semibold text-gray-900">
                            {labelType === 'garment' ? 'Print Garment Tag' : 'Print Customer Receipt'}
                        </h2>
                        <div className="flex items-center gap-2">
                            <button
                                onClick={handlePrint}
                                className="inline-flex items-center px-4 py-2 bg-primary-600 text-white text-sm font-medium rounded-lg hover:bg-primary-700 transition-colors"
                            >
                                🖨️ Print
                            </button>
                            <button
                                onClick={onClose}
                                className="inline-flex items-center px-3 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
                            >
                                ✕
                            </button>
                        </div>
                    </div>

                    {/* Preview area */}
                    <div className="p-6">
                        <p className="text-xs text-gray-500 mb-4 text-center">Preview — optimised for 80mm thermal printer</p>
                        <div className="mx-auto bg-white border-2 border-dashed border-gray-300 rounded-lg overflow-hidden" style={{ maxWidth: '302px' }}>
                            <LabelContent data={data} formatDate={formatDate} formatTime={formatTime} labelType={labelType} />
                        </div>
                    </div>
                </div>
            </div>

            {/* Print-only area */}
            <div className="label-print-area" ref={printRef} style={{ position: 'fixed', left: '-9999px', top: 0 }}>
                <LabelContent data={data} formatDate={formatDate} formatTime={formatTime} labelType={labelType} />
            </div>
        </>
    );
};

/* =========================================
   Label Content — used in both preview & print
   ========================================= */
interface LabelContentProps {
    data: LabelData;
    formatDate: (d?: string) => string;
    formatTime: (d?: string) => string;
    labelType: 'garment' | 'receipt';
}

const LabelContent: React.FC<LabelContentProps> = ({ data, formatDate, formatTime, labelType }) => {
    const labelStyle: React.CSSProperties = {
        fontFamily: "'Courier New', Courier, monospace",
        fontSize: '12px',
        lineHeight: '1.4',
        color: '#000',
        width: '100%',
        padding: '8px',
        boxSizing: 'border-box',
    };

    const hrStyle: React.CSSProperties = {
        border: 'none',
        borderTop: '1px dashed #000',
        margin: '6px 0',
    };

    const boldStyle: React.CSSProperties = {
        fontWeight: 'bold',
    };

    const centerStyle: React.CSSProperties = {
        textAlign: 'center',
    };

    return (
        <div style={labelStyle}>
            {labelType === 'garment' ? (
                /* =============================================
                    PART 1: GARMENT TAG (attach to garment)
                    ============================================= */
                <div style={{ marginBottom: '4px' }}>
                    {/* Header */}
                    <div style={{ ...centerStyle, ...boldStyle, fontSize: '16px', letterSpacing: '1px' }}>
                        ✂ GARMENT TAG
                    </div>
                    <hr style={hrStyle} />

                    {/* Order Number — large and prominent */}
                    <div style={{ ...centerStyle, ...boldStyle, fontSize: '22px', margin: '6px 0 2px' }}>
                        {data.orderNumber}
                    </div>

                    {/* Customer name */}
                    <div style={{ ...centerStyle, fontSize: '14px', ...boldStyle }}>
                        {data.customerName}
                    </div>
                    {data.customerPhone && (
                        <div style={{ ...centerStyle, fontSize: '11px' }}>
                            Tel: {data.customerPhone}
                        </div>
                    )}

                    <hr style={hrStyle} />

                    {/* Date info */}
                    <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '11px' }}>
                        <span>IN: {formatDate(data.createdAt)} {formatTime(data.createdAt)}</span>
                        <span>DUE: {data.dueDate ? formatDate(data.dueDate) : 'TBC'}</span>
                    </div>

                    <hr style={hrStyle} />

                    {/* Items */}
                    <div style={{ ...boldStyle, fontSize: '11px', marginBottom: '2px' }}>
                        ITEMS ({data.itemCount}):
                    </div>
                    {data.items.map((item, i) => (
                        <div key={i} style={{ fontSize: '11px', paddingLeft: '4px' }}>
                            {item.quantity}x {item.name}
                            {item.code ? ` [${item.code}]` : ''}
                            {item.specialInstructions && (
                                <div style={{ fontSize: '10px', paddingLeft: '12px', fontStyle: 'italic' }}>
                                    ⚠ {item.specialInstructions}
                                </div>
                            )}
                        </div>
                    ))}

                    {/* Special instructions */}
                    {data.specialInstructions && (
                        <>
                            <hr style={hrStyle} />
                            <div style={{ fontSize: '11px' }}>
                                <span style={boldStyle}>NOTE: </span>
                                {data.specialInstructions}
                            </div>
                        </>
                    )}

                    <hr style={hrStyle} />

                    {/* Status */}
                    <div style={{ ...centerStyle, fontSize: '11px' }}>
                        Status: <span style={boldStyle}>{data.status.replace('_', ' ').toUpperCase()}</span>
                    </div>
                </div>
            ) : (
                /* =============================================
                    PART 2: CUSTOMER TICKET (hand to customer)
                    ============================================= */
                <div>
                    {/* Site name / branding */}
                    {data.siteName && (
                        <div style={{ ...centerStyle, ...boldStyle, fontSize: '14px' }}>
                            {data.siteName}
                        </div>
                    )}
                    {data.siteAddress && (
                        <div style={{ ...centerStyle, fontSize: '10px' }}>
                            {data.siteAddress}
                        </div>
                    )}
                    {data.sitePhone && (
                        <div style={{ ...centerStyle, fontSize: '10px' }}>
                            Tel: {data.sitePhone}
                        </div>
                    )}

                    <hr style={hrStyle} />

                    <div style={{ ...centerStyle, ...boldStyle, fontSize: '12px', letterSpacing: '1px' }}>
                        🧾 CUSTOMER RECEIPT
                    </div>

                    <hr style={hrStyle} />

                    {/* Order Number */}
                    <div style={{ ...centerStyle, ...boldStyle, fontSize: '18px', margin: '4px 0' }}>
                        {data.orderNumber}
                    </div>

                    {/* Customer */}
                    <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '11px' }}>
                        <span>Customer:</span>
                        <span style={boldStyle}>{data.customerName}</span>
                    </div>

                    <hr style={hrStyle} />

                    {/* Date details */}
                    <div style={{ fontSize: '11px' }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                            <span>Date In:</span>
                            <span>{formatDate(data.createdAt)} {formatTime(data.createdAt)}</span>
                        </div>
                        <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                            <span>Due Date:</span>
                            <span style={boldStyle}>{data.dueDate ? formatDate(data.dueDate) : 'To be confirmed'}</span>
                        </div>
                    </div>

                    <hr style={hrStyle} />

                    {/* Items list */}
                    <div style={{ ...boldStyle, fontSize: '11px', marginBottom: '2px' }}>
                        ITEMS:
                    </div>
                    {data.items.map((item, i) => (
                        <div key={i} style={{ display: 'flex', justifyContent: 'space-between', fontSize: '11px' }}>
                            <span>{item.quantity}x {item.name}</span>
                        </div>
                    ))}

                    <hr style={hrStyle} />

                    {/* Payment info */}
                    <div style={{ fontSize: '11px' }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                            <span>Total:</span>
                            <span style={boldStyle}>£{data.totalAmount.toFixed(2)}</span>
                        </div>
                        <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                            <span>Paid:</span>
                            <span>£{data.paidAmount.toFixed(2)}</span>
                        </div>
                        {data.totalAmount - data.paidAmount > 0.01 && (
                            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                                <span>Balance:</span>
                                <span style={boldStyle}>£{(data.totalAmount - data.paidAmount).toFixed(2)}</span>
                            </div>
                        )}
                    </div>

                    <hr style={hrStyle} />

                    {/* Payment status */}
                    <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '11px' }}>
                        <span>Payment:</span>
                        <span style={boldStyle}>{data.paymentStatus.replace('_', ' ').toUpperCase()}</span>
                    </div>

                    <hr style={hrStyle} />

                    {/* Status */}
                    <div style={{ ...centerStyle, fontSize: '11px' }}>
                        Status: <span style={boldStyle}>{data.status.replace('_', ' ').toUpperCase()}</span>
                    </div>

                    <hr style={hrStyle} />

                    {/* Footer */}
                    <div style={{ ...centerStyle, fontSize: '9px', color: '#666', marginTop: '8px' }}>
                        Thank you for your business!
                    </div>
                </div>
            )}
        </div>
    );
};

export default LabelPrint;
